import { CheckStrokeIcon, Filter, CloseIcon } from '@dynamika-shared/ui/icons';
import {
  CustomDropdownProps,
  Dropdown,
} from '@dynamika-shared/ui/Dropdown/Dropdown';
import { useMemo, useState } from 'react';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { AllFilterItem } from './types';
import { Tooltip } from 'antd';
import classNames from 'classnames';

type MultiFilterProps<Val extends string = string> = CustomDropdownProps & {
  filterName: string;
  filterKey: string;
  allItems: AllFilterItem<Val>[];
  selectedValues: string[];
  onToggleValue: (value: string) => void;
  onClose: () => void;
  active: boolean;
  maxDisplayWidth?: number;
  sortOption?: (a: AllFilterItem<Val>, b: AllFilterItem<Val>) => number;
};

const defaultSortOption = <Val extends string>(
  a: AllFilterItem<Val>,
  b: AllFilterItem<Val>
) => {
  const labelA = a.label.toUpperCase();
  const labelB = b.label.toUpperCase();
  if (labelA < labelB) return -1;
  if (labelA > labelB) return 1;
  return 0;
};

export function MultiFilter<Val extends string = string>(
  props: MultiFilterProps<Val>
) {
  const {
    filterName,
    allItems,
    selectedValues,
    onToggleValue,
    onClose,
    active,
    maxDisplayWidth = 30,
    sortOption = defaultSortOption,
    ...otherProps
  } = props;

  const [visible, setVisible] = useState(false);

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return filterName;
    const valueLabels = selectedValues
      .map(val => allItems.find(item => item.value === val)?.label)
      .filter(Boolean)
      .join(', ');
    return `${filterName}: ${valueLabels}`;
  }, [selectedValues, allItems, filterName]);

  const dropdownItems = useMemo(() => {
    return [
      {
        key: '1',
        type: 'group' as const,
        label: filterName,
        children: [...allItems]
          .sort(sortOption)
          .map(item => ({
            key: item.value,
            label: (
              <div
                className='flex items-center justify-between'
                data-testid={`${item.label}`}
              >
                <CustomTooltip label={item.label} MAX_LABEL_LENGTH={18} />
                {selectedValues.includes(item.value) && (
                  <CheckStrokeIcon width={24} height={24} />
                )}
              </div>
            ),
          })),
      },
    ];
  }, [allItems, selectedValues, filterName, sortOption]);

  if (!active) return null;

  return (
    <div className='sub-filter-container'>
      <div
        role='button'
        className='group/tag bg-waterLightHover50 hover:bg-waterLight flex h-5 cursor-pointer items-center rounded-full p-1'
      >
        <Dropdown
          {...otherProps}
          trigger={['click']}
          open={visible}
          className='h-full p-1'
          onOpenChange={(_, { source }) => {
            if (source === 'trigger') setVisible(!visible);
          }}
          items={dropdownItems}
          onClick={item => onToggleValue(item.key)}
        >
          <div className='flex items-center justify-center gap-1'>
            <Filter
              height={20}
              width={20}
              className='group-hover/tag:text-dodgerBlue'
            />
            <Tooltip
              color='white'
              title={
                displayText.length > 50 ? (
                  <span className='text-1424 text-black'>{displayText}</span>
                ) : undefined
              }
            >
              <div
                className={classNames('text-1424 text-black group-hover/tag:text-dodgerBlue truncate overflow-hidden text-ellipsis whitespace-nowrap', {
                  [`max-w-[${maxDisplayWidth}rem]`]: maxDisplayWidth > 0
                })}
              >
                {displayText}
              </div>
            </Tooltip>
            <CloseIcon
              width={20}
              height={20}
              className='rounded-sm hover:bg-water hover:text-dodgerBlue'
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        </Dropdown>
      </div>
    </div>
  );
}
